import { StoragePolicy as StoragePolicySchema, RedundancyType } from '@schema';
import useAuthStore from '@/common/store/pinia/auth';
import useDomainProjectQuotasStore from '@/common/store/pinia/domainProjectQuotas';
import {
  DEFAULT_POLICY_NAME,
  DEFAULT_STORAGE,
  POLICY_STORAGE_TYPE_MAP,
  POLICY_TYPE, POLICY_TYPE_MAPPING,
  REDUNDANCY_MAP,
  STORAGE_LOCALITY,
  THIN_PROVISIONING_RATIO
} from '@/common/constants/policy.constants';

const computeStoragePolicyMapper = (item: StoragePolicySchema) => {
  const { currentAdminProjectId, currentProject } = useAuthStore();
  const { getProjectStoragePolicyQuotas } = useDomainProjectQuotasStore();

  const { redundancy, storage, name, available, tier, failure_domain, qos, params } = item;
  const { thick_provisioning: thickProvisioningFlag, over_subscription_ratio: overSubscriptionRatio } = item;
  const currentProjectId = currentAdminProjectId || currentProject;

  const thickProvisioningByApi = typeof thickProvisioningFlag === 'boolean'
    ? thickProvisioningFlag
    : typeof overSubscriptionRatio === 'number' && overSubscriptionRatio !== THIN_PROVISIONING_RATIO;

  const quota = getProjectStoragePolicyQuotas(currentProjectId, name);
  const isDisabled = quota?.limit === 0;
  const type = POLICY_STORAGE_TYPE_MAP[storage] || POLICY_TYPE.EXTERNAL;
  const isExternalPolicy = type === POLICY_TYPE.EXTERNAL;
  const { locality: rawLocality, tiers: rawTiers } = item;
  const localTiers = Array.isArray(rawTiers) ? rawTiers : [];
  const locality = rawLocality || STORAGE_LOCALITY.SHARED;
  const isLocalPolicy = !isExternalPolicy && locality === STORAGE_LOCALITY.LOCAL;
  const storageTypeKey = isExternalPolicy ? storage : locality;

  let redundancyChunks;
  let redundancyParityChunks;
  let redundancyItem;
  let redundancyOverhead;
  let redundancyKey;

  if (redundancy) {
    redundancyChunks = redundancy.m;
    redundancyParityChunks = redundancy.n;
    redundancyItem = REDUNDANCY_MAP[redundancy.type][`${redundancy.type}_${redundancyChunks}`]
      || REDUNDANCY_MAP[redundancy.type][`${redundancy.type}_${redundancyChunks}+${redundancyParityChunks}`];

    redundancyOverhead = redundancy.type === RedundancyType.RAID6 ? Math.round((((redundancyChunks + redundancyParityChunks) / redundancyChunks) - 1) * 100) : (redundancyChunks - 1) * 100;
    redundancyKey = redundancyItem?.key === 'raid1_1' || redundancyItem?.key === 'raid6_1+0' ? 'no_redundancy' : redundancyItem?.key;
  }

  return {
    ...item,
    storageName: item.storage === DEFAULT_STORAGE ? POLICY_TYPE_MAPPING[POLICY_TYPE.LOCAL] : item.storage,
    quota,
    type,
    isDisabled,
    isDeletable: name !== DEFAULT_POLICY_NAME,
    failureDomain: failure_domain,
    isExternalPolicy,
    locality,
    isLocalPolicy,
    localTiers,
    localTiersCount: localTiers.length,
    thickProvisioning: isLocalPolicy && thickProvisioningByApi,
    storageTypeKey,
    tier: isExternalPolicy ? '' : (tier || 0),
    redundancyChunks,
    redundancyParityChunks,
    redundancyOverhead,
    redundancyType: redundancy?.type,
    redundancyKey,
    isAvailable: isDisabled ? false : available,
    qosLimitsEnabled: qos && (qos.total_bytes_sec !== -1 || qos.total_iops_sec !== -1),
    detailParams: params || {},
    qos: qos || null,
  };
};

export type computeStoragePolicyModel = ReturnType<typeof computeStoragePolicyMapper>;

export default computeStoragePolicyMapper;
